import { useBackofficeConstant } from "components/context/ConstantProvider";
import useCustomDataSource from "components/ui/customs/useCustomDataSource";
import { useMemo } from "react";

const useVisualisationInformation = ({ fundId, valuationPeriodId }) => {
  const deal_statuses = useBackofficeConstant("backoffice").dealStatuses || [];
  const funds = useBackofficeConstant("backoffice.Fund") || [];
  const round_types = useBackofficeConstant("backoffice").roundTypes || [];
  const asset_types = useBackofficeConstant("operations").assetTypes || [];

  const { useGetByIds } = useCustomDataSource(
    "visualization.FundVisualizationInformation",
  );

  const { data, isSuccess, status } = useGetByIds({
    fundId,
    valuationPeriodId,
  });

  const { stored_portfolios, previous_stored_portfolios } = data || {};

  const buildAssetTree = (fund, deal_status, deal, asset_type, asset) => {
    const portfolio = stored_portfolios.filter(
      (p) =>
        p.fund === fund.id &&
        p.deal_status === deal_status.value &&
        p.deal === deal &&
        p.asset_type === asset_type.value &&
        p.asset === asset,
    );
    if (portfolio.length === 0 || portfolio[0].was_empty) {
      return null;
    } else if (portfolio.length > 1) {
      throw new Error(
        "More than one portfolio for fund " +
          fund.id +
          ", deal status " +
          deal_status.value +
          ", deal " +
          deal +
          ", asset type " +
          asset_type.value +
          " and asset " +
          asset.id,
      );
    }

    const previous_portfolio = previous_stored_portfolios.find(
      (p) =>
        p.fund === fund.id &&
        p.deal_status === deal_status.value &&
        p.deal === deal &&
        p.asset_type === asset_type.value &&
        p.asset === asset,
    );

    return {
      asset,
      portfolio: portfolio[0],
      previous_portfolio,
    };
  };

  const buildAssetTypeTree = (fund, deal_status, deal, asset_type) => {
    const portfolio = stored_portfolios.filter(
      (p) =>
        p.fund === fund.id &&
        p.deal_status === deal_status.value &&
        p.deal === deal &&
        p.asset_type === asset_type.value &&
        p.asset === null,
    );
    if (portfolio.length === 0 || portfolio[0].was_empty) {
      return null;
    } else if (portfolio.length > 1) {
      throw new Error(
        "More than one portfolio for fund " +
          fund.id +
          ", deal status " +
          deal_status.value +
          ", deal " +
          deal +
          " and asset type " +
          asset_type.value,
      );
    }

    const previous_portfolio = previous_stored_portfolios.find(
      (p) =>
        p.fund === fund.id &&
        p.deal_status === deal_status.value &&
        p.deal === deal &&
        p.asset_type === asset_type.value &&
        p.asset === null,
    );

    return {
      asset_type,
      portfolio: portfolio[0],
      previous_portfolio,
      by_asset: stored_portfolios
        .filter(
          (p) =>
            p.fund === fund.id &&
            p.deal_status === deal_status.value &&
            p.deal === deal &&
            p.asset_type === asset_type.value &&
            p.asset !== null,
        )
        .map((p) => {
          return buildAssetTree(fund, deal_status, deal, asset_type, p.asset);
        })
        .filter((n) => n !== null),
    };
  };

  const buildDealTree = (fund, deal_status, deal) => {
    const portfolio = stored_portfolios.filter(
      (p) =>
        p.fund === fund.id &&
        p.deal_status === deal_status.value &&
        p.deal === deal &&
        p.asset_type === "",
    );
    if (portfolio.length === 0 || portfolio[0].was_empty) {
      return null;
    } else if (portfolio.length > 1) {
      throw new Error(
        "More than one portfolio for fund " +
          fund.id +
          ", deal status " +
          deal_status.value +
          " and deal " +
          deal,
      );
    }

    const previous_portfolio = previous_stored_portfolios.find(
      (pp) =>
        pp.fund === fund.id &&
        pp.deal_status === deal_status.value &&
        pp.deal === deal &&
        pp.asset_type === "",
    );

    return {
      portfolio: portfolio[0],
      previous_portfolio,
      by_asset_type: asset_types
        .map((asset_type) => {
          return buildAssetTypeTree(fund, deal_status, deal, asset_type);
        })
        .filter((n) => n !== null),
    };
  };

  const buildRoundTypeTree = (fund, deal_status, round_type) => {
    const portfolio = stored_portfolios.filter(
      (p) =>
        p.fund === fund.id &&
        p.deal_status === deal_status.value &&
        p.round_type === round_type.value &&
        p.deal === null,
    );
    if (portfolio.length === 0 || portfolio[0].was_empty) {
      return null;
    } else if (portfolio.length > 1) {
      throw new Error(
        "More than one portfolio without deal for fund " +
          fund.id +
          ", deal status " +
          deal_status.value +
          " and round type " +
          round_type.value,
      );
    }

    const deals = stored_portfolios
      .filter(
        (p) =>
          p.fund === fund.id &&
          p.deal_status === deal_status.value &&
          p.round_type === round_type.value &&
          p.deal !== null &&
          p.asset_type === "",
      )
      .sort((a, b) => {
        const dateA = a.initial_investment_date || "9999-12-31";
        const dateB = b.initial_investment_date || "9999-12-31";
        if (dateA < dateB) return -1;
        if (dateA > dateB) return 1;
        return 0;
      })
      .map((p) => p.deal);

    return {
      round_type,
      portfolio: portfolio[0],
      previous_portfolio: previous_stored_portfolios.find(
        (p) =>
          p.fund === fund.id &&
          p.deal_status === deal_status.value &&
          p.round_type === round_type.value &&
          p.deal === null,
      ),
      by_deal: deals
        .map((deal) => {
          return buildDealTree(fund, deal_status, deal);
        })
        .filter((n) => n !== null),
    };
  };

  const buildStatusTree = (fund, deal_status) => {
    const portfolio = stored_portfolios.filter(
      (p) =>
        p.fund === fund.id &&
        p.deal_status === deal_status.value &&
        p.round_type === "",
    );
    if (portfolio.length === 0 || portfolio[0].was_empty) {
      return null;
    } else if (portfolio.length > 1) {
      throw new Error(
        "More than one portfolio without deal status, round type and deal for fund " +
          fund.id,
      );
    }

    const deals = stored_portfolios
      .filter(
        (p) =>
          p.fund === fund.id &&
          p.deal_status === deal_status.value &&
          p.deal !== null &&
          p.asset_type === "",
      )
      .sort((a, b) => {
        const dateA = a.initial_investment_date || "9999-12-31";
        const dateB = b.initial_investment_date || "9999-12-31";
        if (dateA < dateB) return -1;
        if (dateA > dateB) return 1;
        return 0;
      })
      .map((p) => p.deal);

    return {
      deal_status,
      portfolio: portfolio[0],
      previous_portfolio: previous_stored_portfolios.find(
        (p) =>
          p.fund === fund.id &&
          p.deal_status === deal_status.value &&
          p.round_type === "",
      ),
      by_round_type: round_types
        .map((round_type) => {
          return buildRoundTypeTree(fund, deal_status, round_type);
        })
        .filter((n) => n !== null),
      by_deal: deals
        .map((deal) => {
          return buildDealTree(fund, deal_status, deal);
        })
        .filter((n) => n !== null),
    };
  };

  const buildFundTree = (fund) => {
    const portfolio = stored_portfolios.filter(
      (p) =>
        p.fund === fund.id &&
        p.deal_status === "" &&
        p.round_type === "" &&
        p.deal === null,
    );
    if (portfolio.length === 0 || portfolio[0].was_empty) {
      return null;
    } else if (portfolio.length > 1) {
      throw new Error(
        "More than one portfolio without deal status, round type and deal for fund " +
          fund.id,
      );
    }

    return {
      fund,
      portfolio: portfolio[0],
      previous_portfolio: previous_stored_portfolios.find(
        (p) =>
          p.fund === fund.id &&
          p.deal_status === "" &&
          p.round_type === "" &&
          p.deal === null,
      ),
      by_deal_status: deal_statuses
        .map((deal_status) => {
          return buildStatusTree(fund, deal_status);
        })
        .filter((n) => n !== null),
    };
  };

  const buildTree = () => {
    return {
      portfolio: stored_portfolios.filter((p) => !p.fund),
      previous_portfolio: previous_stored_portfolios.find((p) => !p.fund),
      by_fund: funds
        .map((fund) => {
          return buildFundTree(fund);
        })
        .filter((n) => n !== null),
    };
  };

  const vizInfo = useMemo(
    () =>
      isSuccess && {
        ...data,
        tree: buildTree(),
      },
    [isSuccess, data],
  );

  return {
    vizInfo,
    isSuccess,
    status,
  };
};

export default useVisualisationInformation;
